export const JUICE_MUTED_STORAGE_KEY = 'juice.muted';
export type JuiceCue = 'save.success' | 'reveal.done' | 'error.soft';

let context: AudioContext | undefined;
let unlocked = false;
let revealPlayed = false;
const lastPlayedAt = new Map<JuiceCue, number>();

export function isJuiceMuted(): boolean { try { return localStorage.getItem(JUICE_MUTED_STORAGE_KEY) === 'true'; } catch { return false; } }
export function setJuiceMuted(muted: boolean): void { try { localStorage.setItem(JUICE_MUTED_STORAGE_KEY, String(muted)); } catch { /* visual feedback remains */ } }

function makeContext(): AudioContext | undefined {
  const Constructor = globalThis.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Constructor) return undefined;
  return context ??= new Constructor();
}

export function installJuiceUnlock(): () => void {
  const unlock = () => { const next = makeContext(); if (next) void next.resume().then(() => { unlocked = true; }).catch(() => undefined); };
  document.addEventListener('pointerdown', unlock, { capture: true, once: true });
  document.addEventListener('keydown', unlock, { capture: true, once: true });
  return () => { document.removeEventListener('pointerdown', unlock, true); document.removeEventListener('keydown', unlock, true); };
}

export function playJuice(cue: JuiceCue): void {
  if (!unlocked || isJuiceMuted() || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (cue === 'reveal.done' && revealPlayed) return;
  const now = Date.now(); if (now - (lastPlayedAt.get(cue) ?? 0) < 150) return;
  const audio = context ?? makeContext(); if (!audio) return;
  try {
    const plan = cue === 'save.success' ? { frequencies: [440], duration: 0.12, gain: 0.018 } : cue === 'reveal.done' ? { frequencies: [523.25, 659.25], duration: 0.45, gain: 0.025 } : { frequencies: [196], duration: 0.15, gain: 0.015 };
    const start = audio.currentTime; const gain = audio.createGain(); gain.gain.setValueAtTime(0.0001, start); gain.gain.exponentialRampToValueAtTime(plan.gain, start + 0.015); gain.gain.exponentialRampToValueAtTime(0.0001, start + plan.duration); gain.connect(audio.destination);
    plan.frequencies.forEach((frequency, index) => { const oscillator = audio.createOscillator(); oscillator.type = 'sine'; oscillator.frequency.setValueAtTime(frequency, start + index * 0.05); oscillator.connect(gain); oscillator.start(start + index * 0.05); oscillator.stop(start + plan.duration); });
    lastPlayedAt.set(cue, now); if (cue === 'reveal.done') revealPlayed = true;
  } catch { /* audio must never block a record */ }
}
